import React, { useState, useEffect, useCallback } from 'react';
import { api } from '../api/client';
import PieChart from './PieChart';
import LineChart from './LineChart';
import BarChart from './BarChart';

const TYPE_COLORS = {
  indica: { background: '#6b46c1', color: '#d6bcfa' },
  sativa: { background: '#2b6cb0', color: '#bee3f8' },
  hybrid: { background: '#c05621', color: '#fefcbf' },
};

const RANGE_OPTIONS = [
  { label: 'All Time', value: 'all' },
  { label: 'Today', value: 'today' },
  { label: 'This Week', value: 'week' },
  { label: 'This Month', value: 'month' },
  { label: 'Custom', value: 'custom' },
];

function getDateRange(range) {
  const now = new Date();
  if (range === 'today') {
    const from = new Date(now.getFullYear(), now.getMonth(), now.getDate()).toISOString();
    return { from };
  }
  if (range === 'week') {
    const day = now.getDay();
    const diff = now.getDate() - day + (day === 0 ? -6 : 1);
    const from = new Date(now.getFullYear(), now.getMonth(), diff).toISOString();
    return { from };
  }
  if (range === 'month') {
    const from = new Date(now.getFullYear(), now.getMonth(), 1).toISOString();
    return { from };
  }
  return {};
}

export default function SalesDashboard() {
  const [summary, setSummary] = useState(null);
  const [byStrain, setByStrain] = useState([]);
  const [overTime, setOverTime] = useState([]);
  const [range, setRange] = useState('all');
  const [customFrom, setCustomFrom] = useState('');
  const [customTo, setCustomTo] = useState('');
  const [currentBatch, setCurrentBatch] = useState(false);
  const [loading, setLoading] = useState(true);
  const [resetting, setResetting] = useState(false);
  const [resetMsg, setResetMsg] = useState(null);

  const fetchData = useCallback(() => {
    setLoading(true);
    let params = {};
    if (currentBatch) {
      params.mode = 'current_batch';
    } else if (range === 'custom') {
      if (customFrom) params.from = new Date(customFrom).toISOString();
      if (customTo) params.to = new Date(customTo + 'T23:59:59').toISOString();
    } else if (range !== 'all') {
      params = getDateRange(range);
    }

    const qs = new URLSearchParams(params).toString();
    const suffix = qs ? `?${qs}` : '';

    Promise.all([
      api.get(`/admin/sales/summary${suffix}`),
      api.get(`/admin/sales/by-strain${suffix}`),
      api.get(`/admin/sales/over-time${suffix}`),
    ]).then(([s, bs, ot]) => {
      setSummary(s);
      setByStrain(bs);
      setOverTime(ot);
    }).catch(() => {}).finally(() => setLoading(false));
  }, [range, customFrom, customTo, currentBatch]);

  useEffect(() => { fetchData(); }, [fetchData]);

  return (
    <div className="card">
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12, flexWrap: 'wrap', gap: 8 }}>
        <h3 style={{ margin: 0 }}>Sales Analytics</h3>
        <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap', alignItems: 'center' }}>
          {RANGE_OPTIONS.map(r => (
            <button
              key={r.value}
              className={!currentBatch && range === r.value ? 'btn-primary btn-small' : 'btn-secondary btn-small'}
              onClick={() => { setRange(r.value); setCurrentBatch(false); }}
              style={{ padding: '4px 10px', fontSize: 11 }}
            >
              {r.label}
            </button>
          ))}
          <button
            className={currentBatch ? 'btn-success btn-small' : 'btn-secondary btn-small'}
            onClick={() => { setCurrentBatch(true); setRange(''); }}
            style={{ padding: '4px 10px', fontSize: 11 }}
          >
            Current Batch
          </button>
          <button
            className="btn-danger btn-small"
            style={{ padding: '4px 10px', fontSize: 11 }}
            disabled={resetting}
            onClick={async () => {
              if (!window.confirm('This will reset all sales & stock analytics.\n\nPoints, spins, and achievements remain untouched.\n\nAre you sure?')) return;
              setResetting(true);
              setResetMsg(null);
              try {
                const data = await api.post('/admin/reset-sales-stats');
                setResetMsg({ type: 'success', text: `Reset complete. ${data.sales_deleted} records cleared.` });
                fetchData();
              } catch (err) {
                setResetMsg({ type: 'error', text: err.message });
              } finally {
                setResetting(false);
              }
            }}
          >
            {resetting ? 'Resetting...' : 'Reset Stats'}
          </button>
        </div>
      </div>

      {resetMsg && (
        <div style={{
          padding: '8px 12px', borderRadius: 'var(--radius-sm)', marginBottom: 12, fontSize: 13,
          background: resetMsg.type === 'success' ? 'rgba(72,187,120,0.1)' : 'rgba(252,129,129,0.1)',
          color: resetMsg.type === 'success' ? 'var(--success)' : 'var(--danger)',
          border: `1px solid ${resetMsg.type === 'success' ? 'var(--success)' : 'var(--danger)'}`,
        }}>
          {resetMsg.text}
        </div>
      )}

      {range === 'custom' && (
        <div style={{ display: 'flex', gap: 8, marginBottom: 12 }}>
          <input type="date" value={customFrom} onChange={e => setCustomFrom(e.target.value)} style={{ fontSize: 12 }} />
          <input type="date" value={customTo} onChange={e => setCustomTo(e.target.value)} style={{ fontSize: 12 }} />
        </div>
      )}

      {loading && <div style={{ textAlign: 'center', padding: 20, color: 'var(--text-muted)' }}>Loading...</div>}

      {!loading && summary && (
        <>
          {/* Summary Stats */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(120px, 1fr))', gap: 8, marginBottom: 16 }}>
            <div className="stat-card" style={{ padding: 12 }}>
              <div className="stat-value" style={{ fontSize: 20 }}>
                {parseFloat(summary.total_revenue).toLocaleString('en-GB', { style: 'currency', currency: 'GBP' })}
              </div>
              <div className="stat-label">Revenue</div>
            </div>
            <div className="stat-card" style={{ padding: 12 }}>
              <div className="stat-value" style={{ fontSize: 20, color: 'var(--warning)' }}>
                {parseFloat(summary.total_cost).toLocaleString('en-GB', { style: 'currency', currency: 'GBP' })}
              </div>
              <div className="stat-label">Cost</div>
            </div>
            <div className="stat-card" style={{ padding: 12 }}>
              <div className="stat-value" style={{ fontSize: 20, color: 'var(--success)' }}>
                {parseFloat(summary.sales_profit).toLocaleString('en-GB', { style: 'currency', currency: 'GBP' })}
              </div>
              <div className="stat-label">Sales Profit</div>
            </div>
            {(parseFloat(summary.reward_cost) > 0 || parseFloat(summary.admin_cost) > 0 || parseFloat(summary.write_off_cost) > 0) && (
              <div className="stat-card" style={{ padding: 12 }}>
                <div className="stat-value" style={{ fontSize: 20, color: parseFloat(summary.total_profit) >= 0 ? 'var(--success)' : 'var(--danger)' }}>
                  {parseFloat(summary.total_profit).toLocaleString('en-GB', { style: 'currency', currency: 'GBP' })}
                </div>
                <div className="stat-label">Net Return</div>
              </div>
            )}
            <div className="stat-card" style={{ padding: 12 }}>
              <div className="stat-value" style={{ fontSize: 20 }}>{summary.avg_margin}%</div>
              <div className="stat-label">Margin</div>
            </div>
            <div className="stat-card" style={{ padding: 12 }}>
              <div className="stat-value" style={{ fontSize: 20 }}>{parseFloat(summary.total_grams)}g</div>
              <div className="stat-label">Grams Sold</div>
            </div>
            <div className="stat-card" style={{ padding: 12 }}>
              <div className="stat-value" style={{ fontSize: 20 }}>{summary.total_orders}</div>
              <div className="stat-label">Orders</div>
            </div>
            {parseFloat(summary.reward_cost) > 0 && (
              <div className="stat-card" style={{ padding: 12 }}>
                <div className="stat-value" style={{ fontSize: 20, color: '#fbd38d' }}>
                  {parseFloat(summary.reward_cost).toLocaleString('en-GB', { style: 'currency', currency: 'GBP' })}
                </div>
                <div className="stat-label">Reward Cost</div>
              </div>
            )}
            {parseFloat(summary.total_stock_cost) > 0 && (
              <div className="stat-card" style={{ padding: 12 }}>
                <div className="stat-value" style={{ fontSize: 20, color: '#d6bcfa' }}>
                  {parseFloat(summary.total_stock_cost).toLocaleString('en-GB', { style: 'currency', currency: 'GBP' })}
                </div>
                <div className="stat-label">Stock Cost</div>
              </div>
            )}
            {parseFloat(summary.left_to_recoup) > 0 && (
              <div className="stat-card" style={{ padding: 12 }}>
                <div className="stat-value" style={{ fontSize: 20, color: 'var(--danger)' }}>
                  {parseFloat(summary.left_to_recoup).toLocaleString('en-GB', { style: 'currency', currency: 'GBP' })}
                </div>
                <div className="stat-label">Left to Make Back</div>
              </div>
            )}
            {parseInt(summary.reward_redemptions) > 0 && (
              <div className="stat-card" style={{ padding: 12 }}>
                <div className="stat-value" style={{ fontSize: 20, color: '#d6bcfa' }}>{summary.reward_redemptions}</div>
                <div className="stat-label">Redeemed</div>
              </div>
            )}
            {summary.write_off_count > 0 && (
              <>
                <div className="stat-card" style={{ padding: 12 }}>
                  <div className="stat-value" style={{ fontSize: 20, color: 'var(--danger)' }}>
                    {parseFloat(summary.write_off_cost).toLocaleString('en-GB', { style: 'currency', currency: 'GBP' })}
                  </div>
                  <div className="stat-label">Written Off</div>
                </div>
                <div className="stat-card" style={{ padding: 12 }}>
                  <div className="stat-value" style={{ fontSize: 20, color: 'var(--danger)' }}>
                    {parseFloat(summary.write_off_grams)}g
                  </div>
                  <div className="stat-label">W/O Grams</div>
                </div>
              </>
            )}
            {summary.consumption_count > 0 && (
              <>
                <div className="stat-card" style={{ padding: 12 }}>
                  <div className="stat-value" style={{ fontSize: 20, color: '#d6bcfa' }}>
                    {parseFloat(summary.consumption_grams)}g
                  </div>
                  <div className="stat-label">Admin Use</div>
                </div>
                <div className="stat-card" style={{ padding: 12 }}>
                  <div className="stat-value" style={{ fontSize: 20, color: '#d6bcfa' }}>
                    {parseFloat(summary.consumption_cost).toLocaleString('en-GB', { style: 'currency', currency: 'GBP' })}
                  </div>
                  <div className="stat-label">Admin Cost</div>
                </div>
              </>
            )}
            {summary.claimed_count > 0 && (
              <>
                <div className="stat-card" style={{ padding: 12 }}>
                  <div className="stat-value" style={{ fontSize: 20, color: '#fbd38d' }}>
                    {parseFloat(summary.claimed_grams)}g
                  </div>
                  <div className="stat-label">Claimed</div>
                </div>
                <div className="stat-card" style={{ padding: 12 }}>
                  <div className="stat-value" style={{ fontSize: 20, color: '#fbd38d' }}>
                    {parseFloat(summary.claimed_cost).toLocaleString('en-GB', { style: 'currency', currency: 'GBP' })}
                  </div>
                  <div className="stat-label">Claimed Cost</div>
                </div>
              </>
            )}
          </div>

          {/* Popular strains */}
          {(summary.most_popular || summary.least_popular) && (
            <div style={{ display: 'flex', gap: 12, marginBottom: 16, fontSize: 13, color: 'var(--text-secondary)' }}>
              {summary.most_popular && <span>Top seller: <strong style={{ color: 'var(--success)' }}>{summary.most_popular}</strong></span>}
              {summary.least_popular && summary.least_popular !== summary.most_popular && (
                <span>Lowest: <strong style={{ color: 'var(--text-muted)' }}>{summary.least_popular}</strong></span>
              )}
            </div>
          )}

          {/* Pie Charts */}
          {byStrain.length > 0 && (
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: 16, marginBottom: 16 }}>
              <PieChart
                label="Revenue by Strain"
                data={byStrain.map(s => ({ label: s.strain_name, value: parseFloat(s.revenue) }))}
                formatValue={v => v.toLocaleString('en-GB', { style: 'currency', currency: 'GBP' })}
              />
              <PieChart
                label="Grams Sold by Strain"
                data={byStrain.map(s => ({ label: s.strain_name, value: parseFloat(s.grams_sold) }))}
                formatValue={v => `${v}g`}
              />
            </div>
          )}

          {/* Line Chart: Revenue / Cost / Net Income over time */}
          {overTime.length > 0 && (
            <div style={{ marginBottom: 16, padding: 12, background: 'var(--bg-secondary)', borderRadius: 'var(--radius-sm)', border: '1px solid var(--border)' }}>
              <LineChart
                label="Financial Trend"
                data={overTime}
                series={[
                  { key: 'revenue', label: 'Revenue', color: '#48bb78' },
                  { key: 'cost', label: 'Cost', color: '#f6ad55' },
                  { key: 'net_income', label: 'Net Income', color: '#63b3ed' },
                ]}
                formatY={v => `£${v.toFixed(0)}`}
              />
            </div>
          )}

          {/* Bar Chart: Daily total grams sold + net return */}
          {overTime.length > 0 && (
            <div style={{ marginBottom: 16, padding: 12, background: 'var(--bg-secondary)', borderRadius: 'var(--radius-sm)', border: '1px solid var(--border)' }}>
              <BarChart
                label="Daily Grams Sold & Net Return"
                data={overTime.map(d => ({
                  label: d.date.slice(5),
                  grams: parseFloat(d.grams_sold),
                  income: parseFloat(d.net_income),
                }))}
                formatIncome={v => `£${Math.abs(v).toFixed(0)}`}
              />
            </div>
          )}

          {/* By Strain Table */}
          {byStrain.length > 0 && (
            <div style={{ overflowX: 'auto' }}>
              <table style={{ fontSize: 13 }}>
                <thead>
                  <tr>
                    <th>Strain</th>
                    <th>Type</th>
                    <th>Grams</th>
                    <th>Admin</th>
                    <th>Rewards</th>
                    <th>Write Offs</th>
                    <th>Stock Cost</th>
                    <th>Revenue</th>
                    <th>Left</th>
                    <th>Profit</th>
                    <th>Margin</th>
                    <th>Orders</th>
                  </tr>
                </thead>
                <tbody>
                  {byStrain.map((s, i) => {
                    const leftToRecoup = parseFloat(s.left_to_recoup);
                    return (
                      <tr key={i}>
                        <td><strong>{s.strain_name}</strong></td>
                        <td><span className="badge" style={TYPE_COLORS[s.type]}>{s.type}</span></td>
                        <td>{parseFloat(s.grams_sold)}g</td>
                        <td style={{ color: parseFloat(s.admin_grams) > 0 ? '#d6bcfa' : 'var(--text-muted)' }}>
                          {parseFloat(s.admin_grams) > 0 ? `${parseFloat(s.admin_grams)}g` : '—'}
                        </td>
                        <td style={{ color: parseFloat(s.reward_grams) > 0 ? '#fbd38d' : 'var(--text-muted)' }}>
                          {parseFloat(s.reward_grams) > 0 ? `${parseFloat(s.reward_grams)}g` : '—'}
                        </td>
                        <td style={{ color: parseFloat(s.write_off_grams) > 0 ? 'var(--danger)' : 'var(--text-muted)' }}>
                          {parseFloat(s.write_off_grams) > 0 ? `${parseFloat(s.write_off_grams)}g` : '—'}
                        </td>
                        <td style={{ color: '#d6bcfa' }}>{parseFloat(s.stock_cost).toLocaleString('en-GB', { style: 'currency', currency: 'GBP' })}</td>
                        <td>{parseFloat(s.revenue).toLocaleString('en-GB', { style: 'currency', currency: 'GBP' })}</td>
                        <td style={{ color: leftToRecoup > 0 ? 'var(--danger)' : 'var(--success)' }}>
                          {leftToRecoup > 0
                            ? leftToRecoup.toLocaleString('en-GB', { style: 'currency', currency: 'GBP' })
                            : 'Paid off'}
                        </td>
                        <td style={{ color: 'var(--success)' }}>{parseFloat(s.profit).toLocaleString('en-GB', { style: 'currency', currency: 'GBP' })}</td>
                        <td>{s.margin}%</td>
                        <td>{s.orders}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}

          {byStrain.length === 0 && (
            <div className="empty-state" style={{ padding: 20 }}>No sales data yet</div>
          )}
        </>
      )}
    </div>
  );
}
